import { use, useEffect } from "react";
import { useParams } from "react-router";
import ForkSettings from "../../components/ForkSettings";
import Form from "../../components/Form";
import Panel from "../../components/Panel";
import ShockSettings from "../../components/ShockSettings";
import Textarea from "../../components/Textarea";
import WheelsSettings from "../../components/WheelsSettings";
import { BikeContext } from "../../context/bikeContext";
import { EditModeContext } from "../../context/editModeContext";
import { usePreferences } from "../../hooks/useAccount";
import { useLatestSettings, useSettingsMutation } from "../../hooks/useSetups";
import { PostSettingsBody } from "../../http/onlyclicksApi";
import { translate } from "../../i18n";
import { Language } from "../../types";
import SubmitBar from "./SubmitBar";

const Settings = () => {
  const params = useParams<"setupId">();
  const setupId = String(params.setupId);
  const { data: latestSettings } = useLatestSettings(setupId);
  const mutation = useSettingsMutation(setupId);
  const { bike } = use(BikeContext);
  const { data: preferences } = usePreferences();
  const i18n = translate(Language.English);
  const editMode = use(EditModeContext);

  useEffect(() => {
    return () => {
      editMode.setOpen(false);
    };
  }, []);

  if (!bike || !preferences) return null;

  return (
    <>
      <Form<PostSettingsBody>
        defaultValues={{
          counting_from: preferences.counting_from,
          fork: {
            sag: bike.fork?.show_sag
              ? latestSettings?.fork?.sag?.curr
              : undefined,
            lbs:
              bike.fork?.type === "coil"
                ? latestSettings?.fork?.lbs?.curr
                : undefined,
            psi:
              bike.fork?.type === "air"
                ? latestSettings?.fork?.psi?.curr
                : undefined,
            lsc: bike.fork?.show_lsc
              ? latestSettings?.fork?.lsc?.curr
              : undefined,
            hsc: bike.fork?.show_hsc
              ? latestSettings?.fork?.hsc?.curr
              : undefined,
            lsr: bike.fork?.show_lsr
              ? latestSettings?.fork?.lsr?.curr
              : undefined,
            hsr: bike.fork?.show_hsr
              ? latestSettings?.fork?.hsr?.curr
              : undefined,
            volume_spacers: bike.fork?.show_volume_spacers
              ? latestSettings?.fork?.volume_spacers?.curr
              : undefined,
            negative_psi: bike.fork?.show_negative_psi
              ? latestSettings?.fork?.negative_psi?.curr
              : undefined,
          },
          shock: {
            sag: bike.shock?.show_sag
              ? latestSettings?.shock?.sag?.curr
              : undefined,
            lbs:
              bike.shock?.type === "coil"
                ? latestSettings?.shock?.lbs?.curr
                : undefined,
            psi:
              bike.shock?.type === "air"
                ? latestSettings?.shock?.psi?.curr
                : undefined,
            lsc: bike.shock?.show_lsc
              ? latestSettings?.shock?.lsc?.curr
              : undefined,
            hsc: bike.shock?.show_hsc
              ? latestSettings?.shock?.hsc?.curr
              : undefined,
            lsr: bike.shock?.show_lsr
              ? latestSettings?.shock?.lsr?.curr
              : undefined,
            hsr: bike.shock?.show_hsr
              ? latestSettings?.shock?.hsr?.curr
              : undefined,
            volume_spacers: bike.shock?.show_volume_spacers
              ? latestSettings?.shock?.volume_spacers?.curr
              : undefined,
          },
          wheels: {
            tire_pressure_unit: preferences.tire_pressure_unit,
            front_bar: latestSettings?.wheels?.front_bar?.curr,
            rear_bar: latestSettings?.wheels?.rear_bar?.curr,
            front_psi: latestSettings?.wheels?.front_psi?.curr,
            rear_psi: latestSettings?.wheels?.rear_psi?.curr,
          },
        }}
        onSubmit={({ value }) =>
          mutation.mutateAsync(value, {
            onSuccess: () => {
              editMode.setOpen(false);
              // TODO: Show notification
            },
          })
        }
      >
        {(form) => (
          <>
            <ForkSettings
              fork={latestSettings?.fork}
              isEditMode={editMode.isOpen}
              form={form}
              countingFrom={preferences.counting_from}
            />
            <ShockSettings
              shock={latestSettings?.shock}
              isEditMode={editMode.isOpen}
              form={form}
              countingFrom={preferences.counting_from}
            />
            <WheelsSettings
              wheels={latestSettings?.wheels}
              isEditMode={editMode.isOpen}
              form={form}
              countingFrom={preferences.counting_from}
            />
            {editMode.isOpen && (
              <Panel title={i18n.notes}>
                <form.Field name="notes">
                  {(field) => (
                    <Textarea
                      name={field.name}
                      value={field.state.value}
                      onChange={(e) => field.handleChange(e.target.value)}
                      disabled={!editMode.isOpen}
                      placeholder="Write an optional note to your setup"
                      fullWidth={true}
                    />
                  )}
                </form.Field>
              </Panel>
            )}
            {editMode.isOpen && (
              <SubmitBar
                isSubmitting={mutation.isPending}
                onCancel={() => {
                  form.reset();
                  editMode.setOpen(false);
                }}
              />
            )}
          </>
        )}
      </Form>
    </>
  );
};

export default Settings;
